<i18n
  src="@/locales/views/agro_exporter/finances/container_outcomes/show_process.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/org_invoice.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/container.yml" lang="yaml"></i18n>

<template>
  <div class="mt-6">
    <h1 class="page-title">
      <i class="fa-solid fa-file-invoice-dollar"></i
      ><span class="space-icon"></span>
      {{ $t("labels.org_invoices_title") }}
    </h1>

    <div class="mt-6 flex flex-row justify-between">
      <div class="panel-plain-label space-x-4">
        <badge-default
          :label="
            org_invoices.length + ' ' + $t('labels.total_number_org_invoices')
          "
        />
        <badge-default
          :label="
            org_invoices_paid +
            ' ' +
            $t('labels.total_number_org_invoices_paid')
          "
        />
      </div>
      <div>
        <button
          type="button"
          @click.prevent="downloadFairtradeReport()"
          class="btn-white"
        >
          <i
            :class="[
              'fa-solid',
              downloading_fairtrade ? 'fa-sync fa-spin' : 'fa-file-excel'
            ]"
          ></i
          ><span class="space-icon"></span
          >{{ $t("labels.download_fairtrade_button") }}
        </button>
        <span class="space-actions"></span>
        <button
          type="button"
          @click.prevent="downloadReport()"
          class="btn-white"
        >
          <i
            :class="[
              'fa-solid',
              downloading ? 'fa-sync fa-spin' : 'fa-file-excel'
            ]"
          ></i
          ><span class="space-icon"></span>{{ $t("labels.download_button") }}
        </button>
      </div>
    </div>

    <div class="mt-2 flex flex-col">
      <ag-grid-vue
        style="height: 600px"
        class="ag-theme-alpine"
        @grid-ready="onGridReady"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="org_invoices_custom"
      >
      </ag-grid-vue>
    </div>
  </div>
</template>

<script>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";
import Requester from "@/views/agro_exporter/requesters/finances/container_outcomes/show_process.js";
import ManageExportInvoiceRequester from "@/views/agro_exporter/requesters/finances/container_outcomes/manage_org_invoice.js";
import PayOrgInvoiceButton from "@/components/views/agro_exporter/finances/container_outcomes/PayOrgInvoiceButton.vue";
import {
  dateFilter,
  booleanFilter
} from "@/components/views/mixins/ag_grid_filters.js";

export default {
  name: "AEFinancesContainerIncomesShowProcessExportInvoices",
  components: { AgGridVue },
  data() {
    return {
      downloading: false,
      downloading_fairtrade: false,
      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: true
      },
      columnDefs: [
        {
          headerName: this.$t("model.container.week"),
          field: "container.week",
          filter: "agNumberColumnFilter",
          maxWidth: 150
        },
        {
          headerName: this.$t("model.org_invoice.invoice_date"),
          valueGetter: params => {
            return Date.parse(params.data.invoice_date);
          },
          valueFormatter: params => {
            return this.dateFormatter(params.data.invoice_date);
          },
          filter: "agDateColumnFilter",
          filterParams: dateFilter
        },
        {
          headerName: this.$t("model.org_invoice.invoice_code"),
          field: "invoice_code",
          filter: true,
          cellRendererSelector: params => {
            const agro_exporter_id = this.agro_exporter_id;
            const agro_exporter_process_id = this.agro_exporter_process_id;
            const GoToLink = {
              template: `<span @click.prevent="this.showExportInvoice()" class="link">${params.value}</span>`,
              methods: {
                showExportInvoice: function () {
                  this.$router.push({
                    name: this.routeName("show_org_invoice"),
                    params: {
                      agro_exporter_id: agro_exporter_id,
                      agro_exporter_process_id: agro_exporter_process_id,
                      org_invoice_id: params.data.id,
                      container_id: params.data.container_id
                    }
                  });
                }
              }
            };
            return {
              component: GoToLink
            };
          }
        },
        {
          headerName: this.$t("model.org_invoice.invoice_issuer"),
          field: "invoice_issuer",
          filter: true
        },
        {
          headerName: this.$t("model.org_invoice.container"),
          field: "container.container_code",
          filter: true
        },
        {
          headerName: this.$t("model.org_invoice.amount"),
          field: "amount",
          filter: "agNumberColumnFilter",
          valueFormatter: params => {
            return this.currencyMoney(
              params.value,
              params.data.currency.symbol
            );
          }
        },
        {
          headerName: this.$t("model.org_invoice.amount_paid"),
          field: "amount_paid",
          filter: "agNumberColumnFilter",
          valueFormatter: params => {
            return this.currencyMoney(
              params.value,
              params.data.currency.symbol
            );
          }
        },
        {
          headerName: this.$t("model.org_invoice.amount_balance"),
          field: "amount_balance",
          filter: "agNumberColumnFilter",
          valueFormatter: params => {
            return this.currencyMoney(
              params.value,
              params.data.currency.symbol
            );
          }
        },
        {
          headerName: this.$t("model.org_invoice.paid"),
          field: "paid",
          filter: "agNumberColumnFilter",
          filterParams: booleanFilter,
          maxWidth: 150,
          cellRendererSelector: params => {
            const YesNoLabel = {
              template: `<yes-no-label :value="${params.value}" />`
            };
            return {
              component: YesNoLabel
            };
          }
        },
        {
          headerName: this.$t("model.org_invoice.fairtrade"),
          field: "fairtrade",
          maxWidth: 150,
          cellRendererSelector: params => {
            const YesNoLabel = {
              template: `<yes-no-label :value="${params.value}" />`
            };
            return {
              component: YesNoLabel
            };
          },
          filter: "agNumberColumnFilter",
          filterParams: booleanFilter
        },
        {
          headerName: this.$t("model.org_invoice.fairtrade_incentive"),
          field: "fairtrade_incentive",
          filter: "agNumberColumnFilter",
          filterParams: booleanFilter,
          maxWidth: 150,
          cellRendererSelector: params => {
            const YesNoLabel = {
              template: `<yes-no-label :value="${params.value}" />`
            };
            return {
              component: YesNoLabel
            };
          }
        },
        {
          headerName: "",
          filter: false,
          cellRendererSelector: params => {
            const payExportInvoice = this.updateExportInvoice;
            const PayButton = {
              template: PayOrgInvoiceButton.template,
              data() {
                return { paid: params.data.paid };
              },
              methods: {
                payExportInvoice: async function () {
                  payExportInvoice(params.data.id);
                }
              }
            };
            return { component: PayButton };
          }
        }
      ]
    };
  },
  props: {
    agro_exporter_id: {},
    agro_exporter_process_id: {},
    org_invoices: {
      type: Array
    }
  },
  computed: {
    org_invoices_paid: function () {
      return this.org_invoices.filter(org_invoice => org_invoice.paid).length;
    },
    org_invoices_custom: function () {
      return this.org_invoices.map(org_invoice => {
        return {
          ...org_invoice
          // invoice_date: this.dateFormatter(org_invoice.invoice_date),
          // paid_label: org_invoice.paid ? ["yes", "si"] : ["no"]
        };
      });
    }
  },
  methods: {
    updateExportInvoice: async function (org_invoice_id) {
      let org_invoice = this.org_invoices.find(
        org_invoice => org_invoice.id == org_invoice_id
      );
      try {
        let response = await ManageExportInvoiceRequester.update(
          this.agro_exporter_id,
          org_invoice.id,
          {
            paid: !org_invoice.paid
          }
        );
        let org_invoice_update = response.data.org_invoice;
        org_invoice.paid = org_invoice_update.paid;
        org_invoice.amount_paid = org_invoice_update.amount_paid;
        org_invoice.amount_balance = org_invoice_update.amount_balance;
        this.registerSuccessNotification({
          title: response.data.success_message
        });
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
    },
    downloadReport: async function () {
      this.downloading = true;
      try {
        var response = await Requester.downloadRepXlsx(
          this.agro_exporter_id,
          this.agro_exporter_process_id
        );
        const file = response.data;
        this.downloadFile(file, "Facturas_y_pagos.xlsx");
      } catch (error) {
        error;
      }
      this.downloading = false;
    },
    downloadFairtradeReport: async function () {
      this.downloading_fairtrade = true;
      try {
        var response = await Requester.downloadFairtradeReport(
          this.agro_exporter_id,
          this.agro_exporter_process_id
        );
        const file = response.data;
        this.downloadFile(file, "Facturas Primas Fairtrade.xlsx");
      } catch (error) {
        error;
      }
      this.downloading_fairtrade = false;
    },
    sizeToFit() {
      //let allColumnIds = [];
      //this.gridColumnApi.getAllColumns().forEach(function (column) {
      //  allColumnIds.push(column.colId);
      //});

      //this.gridColumnApi.autoSizeColumns(allColumnIds);

      this.gridApi.sizeColumnsToFit();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.sizeToFit();
    }
  }
};
</script>
